import { format as dateFnsFormat, parseISO } from "date-fns";
import React from "react";
import { useTranslation } from "react-i18next";
import MuiHelperText from "../MuiHelperText/MuiHelperText";
import QpDatepicker from "../QpDatepicker";

export type SchedulingDateRangePickerError = {
    activationDate?: {
        code: "activationDateInPast";
        message: string;
    };
    expirationDate?: {
        code: "expirationDateInPast" | "expirationBeforeActivation";
        message: string;
    };
}

export type SchedulingDateRangePickerProps = {
    defaultActivationDate: Date;
    defaultExpirationDate: Date | null;
    prefillActivationDate?: Date | null;
    prefillExpirationDate?: Date | null;
    activationDateLabel: string;
    expirationDateLabel: string;
    onActivationDateChange: (date: Date | null) => void;
    onExpirationDateChange: (date: Date | null) => void;
    onError?: (error: SchedulingDateRangePickerError | undefined) => void;
}

const SchedulingDateRangePicker: React.FC<SchedulingDateRangePickerProps> = ({
    defaultActivationDate,
    defaultExpirationDate,
    prefillActivationDate,
    prefillExpirationDate,
    activationDateLabel,
    expirationDateLabel,
    onActivationDateChange,
    onExpirationDateChange,
    onError,
}) => {
    const { t } = useTranslation();
    const [activationDate, setActivationDate] = React.useState<Date | null>(defaultActivationDate);
    const [expirationDate, setExpirationDate] = React.useState<Date | null>(defaultExpirationDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const activationSelectedDate = activationDate
        ? dateFnsFormat(activationDate, "yyyy-MM-dd")
        : undefined;
    const prefillActivationDateStartOfDay =
        prefillActivationDate && new Date(prefillActivationDate);
    prefillActivationDateStartOfDay?.setHours(0, 0, 0, 0);
    const activationDateMinDateSource =
        prefillActivationDate &&
        prefillActivationDateStartOfDay &&
        prefillActivationDateStartOfDay.valueOf() > today.valueOf()
            ? prefillActivationDate
            : today;
    const activationDateMinDate = dateFnsFormat(activationDateMinDateSource, "yyyy-MM-dd");
    const expirationDateMaxDate = prefillExpirationDate
        ? dateFnsFormat(prefillExpirationDate, "yyyy-MM-dd")
        : undefined;
    const expirationSelectedDate = expirationDate
        ? dateFnsFormat(expirationDate, "yyyy-MM-dd")
        : undefined;
    const expirationMinDate =
        activationDate && today.valueOf() > activationDate.valueOf()
            ? dateFnsFormat(today, "yyyy-MM-dd")
            : activationSelectedDate;
    const isActivationDateInThePast = defaultActivationDate.valueOf() < today.valueOf();
    const isActivationDateInvalid =
        !isActivationDateInThePast &&
        !!activationDate &&
        activationDate.valueOf() < today.valueOf();
    const isExpirationDateInThePast =
        !!expirationDate && expirationDate.valueOf() < today.valueOf();
    const isExpirationDateBeforeActivation =
        !!expirationDate && !!activationDate && expirationDate.valueOf() < activationDate.valueOf();
    const isExpirationDateInvalid = isExpirationDateInThePast || isExpirationDateBeforeActivation;
    const error = React.useMemo<SchedulingDateRangePickerError | undefined>(() => {
        if (!isActivationDateInvalid && !isExpirationDateInvalid) {
            return undefined;
        }

        return {
            activationDate: isActivationDateInvalid
                ? { code: "activationDateInPast", message: t("vehicleForm:activationDateNotInPast") }
                : undefined,
            expirationDate: isExpirationDateInvalid
                ? isExpirationDateInThePast
                    ? { code: "expirationDateInPast", message: t("vehicleForm:expirationDateNotInPast") }
                    : {
                          code: "expirationBeforeActivation",
                          message: t("vehicleForm:expirationBeforeActivation"),
                      }
                : undefined,
        };
    }, [isActivationDateInvalid, isExpirationDateInvalid, isExpirationDateInThePast, t]);

    React.useEffect(() => {
        onError?.(error);
    }, [error, onError]);

    return (
        <div style={{ display: "flex", marginLeft: "32px", marginTop: "12px", gap: 8 }}>
            <div>
                <QpDatepicker
                    defaultSelectedDate={activationSelectedDate}
                    minDate={activationDateMinDate}
                    maxDate={expirationDateMaxDate}
                    label={activationDateLabel}
                    onChange={(date) => {
                        const selectedDate = date ? parseISO(date) : null;
                        setActivationDate(selectedDate);
                        onActivationDateChange(selectedDate);
                    }}
                    readonly={isActivationDateInThePast}
                />
                <MuiHelperText error={error?.activationDate?.message} />
            </div>
            <div>
                <QpDatepicker
                    defaultSelectedDate={expirationSelectedDate}
                    minDate={expirationMinDate}
                    maxDate={expirationDateMaxDate}
                    label={expirationDateLabel}
                    onChange={(date) => {
                        const selectedDate = date ? parseISO(date) : null;
                        setExpirationDate(selectedDate);
                        onExpirationDateChange(selectedDate);
                    }}
                />
                <MuiHelperText error={error?.expirationDate?.message} />
            </div>
        </div>
    );
};

export default SchedulingDateRangePicker;
